<template>
  <div class="voice-settings q-gutter-sm q-pa-md">
    <div class="text-subtitle2 q-ml-none">Voice Settings</div>
    <!-- Macro: Gender Tilt -->
    <div class="row q-col-gutter-md q-mb-sm">
      <div class="col-12">
        <div class="slider-row">
          <div class="label">Gender Tilt</div>
          <q-slider
            v-model.number="voiceLocal.genderTilt"
            :min="-100"
            :max="100"
            :step="5"
            label
            label-always
            :label-value="genderTiltLabel"
            @update:model-value="applyGenderTilt"
          />
        </div>
      </div>
    </div>
    <div class="row q-col-gutter-md">
      <div class="col-12 col-sm-6">
        <div class="slider-row">
          <div class="label">Bass (dB)</div>
          <q-slider
            v-model.number="voiceLocal.bass"
            :min="0"
            :max="12"
            :step="1"
            label
            label-always
            :label-value="voiceLocal.bass + ' dB'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Bass Freq (Hz)</div>
          <q-slider
            v-model.number="voiceLocal.bassFrequency"
            :min="60"
            :max="320"
            :step="10"
            label
            label-always
            :label-value="voiceLocal.bassFrequency + ' Hz'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Pitch (semitones)</div>
          <q-slider
            v-model.number="voiceLocal.pitch"
            :min="-12"
            :max="12"
            :step="1"
            label
            label-always
            :label-value="(voiceLocal.pitch > 0 ? '+' : '') + voiceLocal.pitch + ' st'"
          />
        </div>
      </div>
      <div class="col-12 col-sm-6">
        <div class="slider-row">
          <div class="label">Echo</div>
          <q-slider
            v-model.number="voiceLocal.echo"
            :min="0"
            :max="0.9"
            :step="0.05"
            label
            label-always
            :label-value="voiceLocal.echo.toFixed(2)"
          />
        </div>
        <div class="slider-row">
          <div class="label">Echo Delay (s)</div>
          <q-slider
            v-model.number="voiceLocal.echoDelay"
            :min="0"
            :max="0.5"
            :step="0.01"
            label
            label-always
            :label-value="voiceLocal.echoDelay.toFixed(2) + ' s'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Formant (st)</div>
          <q-slider
            v-model.number="voiceLocal.formant"
            :min="-12"
            :max="12"
            :step="1"
            label
            label-always
            :label-value="(voiceLocal.formant > 0 ? '+' : '') + voiceLocal.formant + ' st'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Brightness (dB)</div>
          <q-slider
            v-model.number="voiceLocal.brightness"
            :min="-6"
            :max="6"
            :step="0.5"
            label
            label-always
            :label-value="(voiceLocal.brightness > 0 ? '+' : '') + voiceLocal.brightness + ' dB'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Presence (dB)</div>
          <q-slider
            v-model.number="voiceLocal.presence"
            :min="-6"
            :max="12"
            :step="0.5"
            label
            label-always
            :label-value="(voiceLocal.presence > 0 ? '+' : '') + voiceLocal.presence + ' dB'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Intensity</div>
          <q-slider
            v-model.number="voiceLocal.timbreIntensity"
            :min="0"
            :max="2"
            :step="0.1"
            label
            label-always
            :label-value="voiceLocal.timbreIntensity.toFixed(1) + 'x'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Wet</div>
          <q-slider
            v-model.number="voiceLocal.wet"
            :min="0"
            :max="1"
            :step="0.05"
            label
            label-always
            :label-value="Math.round(voiceLocal.wet * 100) + '%'"
          />
        </div>
        <div class="slider-row">
          <div class="label">Dry</div>
          <q-slider
            v-model.number="voiceLocal.dry"
            :min="0"
            :max="1"
            :step="0.05"
            label
            label-always
            :label-value="Math.round(voiceLocal.dry * 100) + '%'"
          />
        </div>
        <div class="row items-center q-gutter-sm q-mt-sm">
          <q-toggle v-model="voiceLocal.vocoderMode" label="Vocoder mode" />
        </div>
      </div>
    </div>

    <div class="row items-center q-gutter-sm">
      <q-btn :label="isTesting ? 'Stop Test' : 'Start Test'" color="primary" @click="toggleTest" />
      <q-toggle v-model="monitorEnabled" label="Monitor locally" />
      <q-toggle v-model="bypass" label="Bypass" @update:model-value="updateMonitorRoute" />
      <div class="row items-center q-gutter-xs">
        <q-btn dense outline label="Neutral" @click="applyPreset('neutral')" />
        <q-btn dense outline label="Deep" @click="applyPreset('deep')" />
        <q-btn dense outline label="Light" @click="applyPreset('light')" />
      </div>
      <q-space />
      <q-btn
        flat
        dense
        color="secondary"
        icon="restart_alt"
        label="Reset to Neutral"
        @click="resetToNeutral"
      />
      <div class="text-caption text-grey-6">
        Tip: Combine Pitch and Formant to shift deep/light without chipmunk/robot artifacts. Brightness/Presence adjust clarity.
      </div>
    </div>

    <audio ref="monitorEl" autoplay playsinline style="display: none"></audio>
  </div>
</template>

<script setup>
  import { ref, watch, onBeforeUnmount, computed } from 'vue'
  import audioEngine from '@/components/plugins/AvatarChat/services/audioEngine'

  const props = defineProps({
    voice: {
      type: Object,
      default: () => ({})
    }
  })
  const emit = defineEmits(['update:voice'])

  const defaults = {
    // Neutral/no-effect defaults
    bass: 0,
    bassFrequency: 120,
    pitch: 0,
    formant: 0,
    brightness: 0,
    presence: 0,
    timbreIntensity: 1,
    genderTilt: 0,
    echo: 0,
    echoDelay: 0.18,
    wet: 0,
    dry: 1
  }

  const voiceLocal = ref({ ...defaults, ...(props.voice || {}) })
  const genderTiltLabel = computed(() => {
    const t = Number(voiceLocal.value?.genderTilt || 0)
    return t < 0 ? 'Deep' : t > 0 ? 'Light' : 'Neutral'
  })
  const ignoreNextLocalChange = ref(false)
  watch(
    () => props.voice,
    (v) => {
      const next = { ...defaults, ...(v || {}) }
      // avoid resetting sliders while actively editing; shallow merge
      ignoreNextLocalChange.value = true
      voiceLocal.value = { ...voiceLocal.value, ...next }
      try {
        if (graph.value) graph.value.updateSettings(next)
      } catch {}
    },
    { deep: true }
  )

  watch(
    () => voiceLocal.value,
    (v) => {
      if (ignoreNextLocalChange.value) {
        ignoreNextLocalChange.value = false
        return
      }
      const payload = { ...defaults, ...(v || {}) }
      // Only emit when different from incoming prop to avoid feedback loops
      try {
        const a = JSON.stringify(payload)
        const b = JSON.stringify({ ...defaults, ...(props.voice || {}) })
        if (a === b) {
          // Still update graph if running
          try {
            if (graph.value) graph.value.updateSettings(v || {})
          } catch {}
          return
        }
      } catch {}
      emit('update:voice', payload)
      try {
        if (graph.value) graph.value.updateSettings(v || {})
      } catch {}
    },
    { deep: true }
  )

  const isTesting = ref(false)
  const monitorEnabled = ref(true)
  const monitorEl = ref(null)
  const audioCtx = ref(null)
  const micStream = ref(null)
  const graph = ref(null)
  const bypass = ref(false)

  function updateMonitorRoute() {
    if (!monitorEl.value) return
    try {
      if (bypass.value) {
        monitorEl.value.srcObject = micStream.value || null
      } else if (graph.value) {
        monitorEl.value.srcObject = graph.value.destinationStream
      }
      void monitorEl.value.play?.()
    } catch {}
  }

  function resetToNeutral() {
    // Restore to neutral/no-effect defaults and update running graph
    voiceLocal.value = { ...defaults }
    try {
      if (graph.value && typeof graph.value.updateSettings === 'function') {
        graph.value.updateSettings(voiceLocal.value)
      }
    } catch {}
  }

  // Macro control: maps tilt [-100..100] to pitch/formant/brightness/presence
  function applyGenderTilt(val) {
    const t = Math.max(-100, Math.min(100, Number(val) || 0))
    // Emphasize formant over pitch for more natural timbre change
    const pitch = Math.round((t / 100) * 4) // ±4 st
    const formant = Math.round((t / 100) * 8) // ±8 st
    // 0.5 dB steps
    const round05 = (x) => Math.round(x * 2) / 2
    const brightness = round05((t / 100) * 2) // ±2 dB
    const presence = round05((t / 100) * 4) // ±4 dB
    voiceLocal.value = {
      ...voiceLocal.value,
      genderTilt: t,
      pitch,
      formant,
      brightness,
      presence
    }
    try {
      if (graph.value && typeof graph.value.updateSettings === 'function') {
        graph.value.updateSettings({ pitch, formant, brightness, presence })
      }
    } catch {}
  }

  function applyPreset(kind) {
    const clamp = (n, min, max) => Math.min(max, Math.max(min, n))
    if (kind === 'neutral') {
      resetToNeutral()
      return
    }
    if (kind === 'deep') {
      const next = {
        ...voiceLocal.value,
        pitch: -3,
        formant: -6,
        brightness: -1.5,
        presence: 2,
        bass: clamp((voiceLocal.value.bass ?? 0) + 2, 0, 12),
        bassFrequency: 120
      }
      voiceLocal.value = next
      try {
        graph.value?.updateSettings?.(next)
      } catch {}
      return
    }
    if (kind === 'light') {
      const next = {
        ...voiceLocal.value,
        pitch: 3,
        formant: 6,
        brightness: 1.5,
        presence: 3,
        bass: clamp((voiceLocal.value.bass ?? 0), 0, 12),
        bassFrequency: 140
      }
      voiceLocal.value = next
      try {
        graph.value?.updateSettings?.(next)
      } catch {}
      return
    }
  }

  async function startTest() {
    if (isTesting.value) return
    try {
      const ms = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      })
      micStream.value = ms
    } catch (err) {
      console.warn('[VoiceSettings] getUserMedia failed', err)
      return
    }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      audioCtx.value = ctx
      const g = audioEngine.createVoiceGraph(ctx, micStream.value, voiceLocal.value)
      graph.value = g

      if (monitorEnabled.value && monitorEl.value) {
        try {
          monitorEl.value.srcObject = bypass.value ? micStream.value : g.destinationStream
          await monitorEl.value.play().catch(() => {})
        } catch {}
      }
      isTesting.value = true
    } catch (err) {
      console.warn('[VoiceSettings] init audio graph failed', err)
      stopTest()
    }
  }

  function stopTest() {
    if (!isTesting.value) return
    try {
      if (monitorEl.value) {
        try {
          monitorEl.value.pause()
          monitorEl.value.srcObject = null
        } catch {}
      }
      if (graph.value) {
        try {
          graph.value.stop()
        } catch {}
        graph.value = null
      }
      const tracks = micStream.value?.getTracks?.() || []
      tracks.forEach((t) => {
        try {
          t.stop()
        } catch {}
      })
      micStream.value = null
      if (audioCtx.value) {
        try {
          audioCtx.value.close?.()
        } catch {}
        audioCtx.value = null
      }
    } finally {
      isTesting.value = false
    }
  }

  function toggleTest() {
    if (isTesting.value) stopTest()
    else startTest()
  }

  onBeforeUnmount(() => stopTest())
</script>

<style scoped>
  .voice-settings {
    min-width: 280px;
    max-width: 640px;
  }
  .slider-row {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 10px;
    align-items: center;
    margin: 6px 0;
  }
  .label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
  }
</style>
